import {
  Button,
  Caption1,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  InputOnChangeData,
  Spinner,
  Text,
  Textarea,
  TextareaOnChangeData,
  Tooltip,
  mergeClasses,
} from "@fluentui/react-components";
import {
  ArrowDownloadRegular,
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkRegular,
  DeleteRegular,
  DismissRegular,
  DocumentRegular,
} from "@fluentui/react-icons";
import {
  createDiagnostic,
  deleteDiagnosticLogo,
  registerDiagnosticLogo,
  updateDiagnostic,
  updateDiagnosticOwner,
} from "apps/diagnostics/api";
import {
  ICreateDiagnostic,
  IDiagnostic,
} from "apps/diagnostics/models/IDiagnostic";
import { ALLOWED_FILE_EXTENSIONS, ConstantValues } from "common/constants";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { IAzureUser } from "models/IAzureUser";
import { IUser } from "models/IUser";
import { IUserDetails } from "models/IUserDetails";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedFileCardStyles,
} from "theme/sharedStyles";
import { downloadBlobAsFile, triggerBrowserDownload } from "utils/blobUtils";
import { mapAzureUsersToModel, toUserOption } from "utils/generalUtils";
import {
  addDiagnostic,
  updateDiagnosticInSlice,
} from "../../../../../redux/diagnosticsSlice";
import { IEditDiagnosticsProps } from "./editDiagnostic.types";

import { addDiagnosticCreated } from "api";
import { RichTextField } from "components/cells/RichTextFieldCell";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { FileDropzone } from "components/fileDropzone/FileDropzone";
import { ColumnTypes } from "models/enums/ColumnTypes";
import { FileWithId } from "models/FileWithId";
import { IFile } from "models/IFile";
import { useNavigate } from "react-router-dom";
import { designTokens } from "theme/customTheme";
import { uploadFilesToStorage, uploadLogoToStorage } from "utils/blobUtils";
import { getFieldValidation } from "utils/dialogUtils";
import { buildAccentPalette } from "utils/accentPalette";
import { LabelWithInfo } from "../../labelWithInfo/LabelWithInfo";
import { DemographicsTab } from "./DemographicsTab";
import { BrandingPreview } from "./brandingPreview/BrandingPreview";
import { useStyles } from "./editDiagnostic.styles";
import { DiagnosticTab } from "./editDiagnostic.types";

const FIELD_LIMITS = {
  NAME_MAX: 150,
  NAME_WARN: 120,
  DESCRIPTION_MAX: 1024,
  DESCRIPTION_WARN: 900,
  BACKGROUND_MAX: 1024,
  BACKGROUND_WARN: 900,
} as const;

export const EditDiagnostic = (props: IEditDiagnosticsProps): JSX.Element => {
  const navigate = useNavigate();
  const styles = useStyles();
  const contentStyles = useSharedDialogContentStyles();
  const dialogFooterStyles = useSharedDialogFooterStyles();
  const fileCardStyles = useSharedFileCardStyles();

  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );
  const diagnostics: IDiagnostic[] = useSelector(
    (state: RootState) => state.diagnosticsSlice.value,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const dispatch = useDispatch();

  const isEditMode = !!props.diagnostic;

  const [name, setName] = useState(
    props.diagnostic?.name || ConstantValues.EMPTY_STRING,
  );
  const [description, setDescription] = useState(
    props.diagnostic?.description || ConstantValues.EMPTY_STRING,
  );
  const [client, setClient] = useState(
    props.diagnostic?.client || ConstantValues.EMPTY_STRING,
  );
  const [background, setBackground] = useState(
    props.diagnostic?.background || ConstantValues.EMPTY_STRING,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [beginningMessage, setBeginningMessage] = useState(
    props.diagnostic?.beginningMessage || ConstantValues.EMPTY_STRING,
  );
  const [endingMessage, setEndingMessage] = useState(
    props.diagnostic?.endingMessage || ConstantValues.EMPTY_STRING,
  );
  const [headerColor, setHeaderColor] = useState(
    props.diagnostic?.headerColor || designTokens.primary[900],
  );
  const [accentColor, setAccentColor] = useState(
    props.diagnostic?.accentColor || designTokens.accent[500],
  );
  const [headerLogoUrl, setHeaderLogoUrl] = useState(
    props.diagnostic?.headerLogoUrl || ConstantValues.EMPTY_STRING,
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const originalLogoUrl = props.diagnostic?.headerLogoUrl;
  const [customBrandingEnabled, setCustomBrandingEnabled] = useState(
    props.diagnostic?.customBrandingEnabled ?? false,
  );
  const [demographicsQuestions, setDemographicsQuestions] = useState<string[]>(
    props.diagnostic?.demographicsQuestions ?? [],
  );
  const [demographicsEnabled, setDemographicsEnabled] = useState<boolean>(
    (props.diagnostic?.demographicsQuestions?.length ?? 0) > 0,
  );
  const [demographicsPlacement, setDemographicsPlacement] = useState<
    "beginning" | "end"
  >(props.diagnostic?.demographicsPlacement ?? "beginning");
  const [demographicsQuestionOverrides, setDemographicsQuestionOverrides] =
    useState<Record<string, string>>(
      props.diagnostic?.demographicsQuestionOverrides ?? {},
    );
  const [selectedCollaborators, setSelectedCollaborators] = useState<
    CustomOption[]
  >(
    props.diagnostic?.collaborators?.map((c) => ({
      id: c.userId,
      key: c.userId,
      text: c.displayName,
    })) ?? [],
  );

  const originalOwner = props.diagnostic?.user;
  const [selectedOwner, setSelectedOwner] = useState<CustomOption[]>(() => {
    if (originalOwner) {
      return [toUserOption(originalOwner.userId, originalOwner.displayName)];
    }
    if (props.isDiagnosticsAdmin && currentUser?.id) {
      return [toUserOption(currentUser.id, currentUser.displayName)];
    }
    return [];
  });

  const [existingAttachments, setExistingAttachments] = useState<IFile[]>(
    props.diagnostic?.attachments ?? [],
  );

  const [localFiles, setLocalFiles] = useState<FileWithId[]>([]);

  const [isDownloading, setIsDownloading] = useState(false);

  const [activeTab, setActiveTab] = useState<DiagnosticTab>("details");

  const tabOrder = useMemo<DiagnosticTab[]>(() => {
    const tabs: DiagnosticTab[] = ["details", "context", "messages"];
    if (customBrandingEnabled) tabs.push("branding");
    if (demographicsEnabled) tabs.push("demographics");
    return tabs;
  }, [customBrandingEnabled, demographicsEnabled]);

  const navigateTab = (target: DiagnosticTab): void => {
    const currentIdx = tabOrder.indexOf(activeTab);
    const targetIdx = tabOrder.indexOf(target);
    if (targetIdx > currentIdx && !validate()) return;
    setActiveTab(target);
  };

  const handleChange = useCallback(
    (setter: (v: string) => void, fieldKey: string) =>
      (_: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setter(data.value);
        setFieldErrors((prev) => {
          const { [fieldKey]: _, ...rest } = prev;
          return rest;
        });
      },
    [],
  );

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!name.trim()) {
      errors.name = "Name is required.";
    } else if (name.length > FIELD_LIMITS.NAME_MAX) {
      errors.name = `Name must be ${FIELD_LIMITS.NAME_MAX} characters or fewer.`;
    }

    if (!description.trim()) {
      errors.description = "Engagement Details is required.";
    } else if (description.length > FIELD_LIMITS.DESCRIPTION_MAX) {
      errors.description = `Engagement Details must be ${FIELD_LIMITS.DESCRIPTION_MAX} characters or fewer.`;
    }

    if (!client.trim()) {
      errors.client = "Client is required.";
    }

    if (props.isDiagnosticsAdmin && selectedOwner.length === 0) {
      errors.owner = "Owner is required.";
    }

    const conflict: boolean = diagnostics.some(
      (d) =>
        d.name.trim().toLowerCase() === name.trim().toLowerCase() &&
        (!isEditMode || d.id !== props.diagnostic?.id),
    );
    if (conflict) {
      errors.name = "A diagnostic with this name already exists.";
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      if (errors.name || errors.description || errors.client || errors.owner) {
        setActiveTab("details");
      }
      return false;
    }
    return true;
  };

  const previewPalette = useMemo(
    () => buildAccentPalette(accentColor, headerColor),
    [accentColor, headerColor],
  );

  const collaboratorsPayload = useMemo(
    () =>
      selectedCollaborators.map((o) => ({
        userId: o.id ?? ConstantValues.EMPTY_STRING,
        displayName: o.text,
      })),
    [selectedCollaborators],
  );

  const selectedOwnerPayload = useMemo(() => {
    if (!props.isDiagnosticsAdmin || selectedOwner.length === 0) {
      return undefined;
    }
    const owner = selectedOwner[0];
    return {
      userId: owner.id ?? owner.key,
      displayName: owner.text,
    };
  }, [props.isDiagnosticsAdmin, selectedOwner]);

  const applyOwnerChangeIfNeeded = async (
    diagnosticId: string,
    currentOwner: IUserDetails | undefined,
  ): Promise<{ owner: IUserDetails | undefined; failed: boolean }> => {
    const selectedOwnerId = selectedOwner[0]?.id ?? selectedOwner[0]?.key;
    const hasOwnerChanged =
      props.isDiagnosticsAdmin &&
      !!selectedOwnerId &&
      selectedOwnerId !== originalOwner?.userId;

    if (!hasOwnerChanged) {
      return { owner: currentOwner, failed: false };
    }

    const newOwner: IUserDetails = {
      userId: selectedOwnerId,
      displayName: selectedOwner[0].text,
    };
    try {
      await updateDiagnosticOwner({ id: diagnosticId, owner: newOwner });
      return { owner: newOwner, failed: false };
    } catch {
      return { owner: currentOwner, failed: true };
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!validate()) {
      return;
    }

    let ownerUpdateFailed = false;
    setIsLoading(true);
    try {
      if (isEditMode) {
        const diagnosticId = props.diagnostic!.id;
        let resolvedLogoUrl = headerLogoUrl;

        if (
          logoFile &&
          originalLogoUrl?.startsWith("http") &&
          props.diagnostic?.id
        ) {
          try {
            await deleteDiagnosticLogo(props.diagnostic.id);
          } catch (e) {
            console.warn("Failed to delete old logo blob:", e);
          }
        }

        if (logoFile && props.diagnostic?.id) {
          const blobName = await uploadLogoToStorage(
            logoFile,
            "diagnostics-logo",
            diagnosticId,
          );
          const logoResult = await registerDiagnosticLogo(
            diagnosticId,
            blobName,
          );
          resolvedLogoUrl = logoResult.url;
        }

        const filesToUpload = localFiles.filter(
          (f) => f.file instanceof File && f.file.size > 0,
        );
        let uploaded: IFile[] = [];
        if (filesToUpload.length) {
          uploaded = await uploadFilesToStorage(
            filesToUpload,
            "diagnostics",
            diagnosticId,
          );
        }

        const finalAttachments: IFile[] = [...existingAttachments, ...uploaded];

        const payload: ICreateDiagnostic = {
          ...props.diagnostic,
          name,
          description,
          client,
          background,
          collaborators: collaboratorsPayload,
          beginningMessage,
          endingMessage,
          headerColor,
          accentColor,
          headerLogoUrl: resolvedLogoUrl || undefined,
          customBrandingEnabled,
          demographicsQuestions: demographicsEnabled
            ? demographicsQuestions
            : [],
          demographicsPlacement: demographicsEnabled
            ? demographicsPlacement
            : "beginning",
          demographicsQuestionOverrides: demographicsEnabled
            ? demographicsQuestionOverrides
            : {},
          attachments: finalAttachments,
        };

        await updateDiagnostic({ id: diagnosticId, diagnostic: payload });

        const { owner: updatedOwner, failed: ownerChangeFailed } =
          await applyOwnerChangeIfNeeded(diagnosticId, props.diagnostic?.user);
        ownerUpdateFailed = ownerChangeFailed;

        dispatch(
          updateDiagnosticInSlice({
            ...props.diagnostic!,
            name,
            description,
            client,
            background,
            collaborators: collaboratorsPayload,
            beginningMessage,
            endingMessage,
            headerColor,
            accentColor,
            headerLogoUrl: resolvedLogoUrl || undefined,
            customBrandingEnabled,
            demographicsQuestions: demographicsEnabled
              ? demographicsQuestions
              : [],
            demographicsPlacement: demographicsEnabled
              ? demographicsPlacement
              : "beginning",
            demographicsQuestionOverrides: demographicsEnabled
              ? demographicsQuestionOverrides
              : {},
            attachments: finalAttachments,
            user: updatedOwner ?? props.diagnostic!.user,
          }),
        );
      } else {
        const created = await createDiagnostic({
          name,
          description,
          client,
          background,
          owner: selectedOwnerPayload,
          collaborators: collaboratorsPayload,
          beginningMessage,
          endingMessage,
          headerColor,
          accentColor,
          headerLogoUrl: headerLogoUrl || undefined,
          customBrandingEnabled,
          demographicsQuestions: demographicsEnabled
            ? demographicsQuestions
            : [],
          demographicsPlacement: demographicsEnabled
            ? demographicsPlacement
            : "beginning",
          demographicsQuestionOverrides: demographicsEnabled
            ? demographicsQuestionOverrides
            : {},
        });

        await addDiagnosticCreated({
          diagnosticId: created._id?.toString() || ConstantValues.EMPTY_STRING,
        });

        const filesToUpload = localFiles.filter(
          (f) => f.file instanceof File && f.file.size > 0,
        );
        let uploaded: IFile[] = [];
        if (filesToUpload.length && created.id) {
          uploaded = await uploadFilesToStorage(
            filesToUpload,
            "diagnostics",
            created.id,
          );
        }

        let createdLogoUrl = headerLogoUrl;
        if (logoFile && created.id) {
          const blobName = await uploadLogoToStorage(
            logoFile,
            "diagnostics-logo",
            created.id,
          );
          const logoResult = await registerDiagnosticLogo(created.id, blobName);
          createdLogoUrl = logoResult.url;
        }

        const finalAttachments: IFile[] = [...uploaded];
        const createdWithFiles: IDiagnostic = await updateDiagnostic({
          id: created.id,
          diagnostic: {
            ...created,
            name,
            description,
            client,
            background,
            collaborators: collaboratorsPayload,
            beginningMessage,
            endingMessage,
            customBrandingEnabled,
            demographicsQuestions: demographicsEnabled
              ? demographicsQuestions
              : [],
            demographicsPlacement: demographicsEnabled
              ? demographicsPlacement
              : "beginning",
            demographicsQuestionOverrides: demographicsEnabled
              ? demographicsQuestionOverrides
              : {},
            headerColor,
            accentColor,
            attachments: finalAttachments,
            headerLogoUrl: createdLogoUrl || undefined,
          },
        });

        if (createdWithFiles.id) {
          dispatch(addDiagnostic(createdWithFiles));
          navigate(`/diagnostic/${createdWithFiles.id}`);
        }
      }

      if (ownerUpdateFailed) {
        setFieldErrors({
          general:
            "Diagnostic saved, but ownership could not be reassigned. Please try again.",
        });
        return;
      }

      props.onDismiss(true);
    } catch (e) {
      console.error("Error saving diagnostic:", e);
      setFieldErrors({
        general: "An error occurred while saving. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDescriptionChange = (
    _: ChangeEvent<HTMLTextAreaElement>,
    data: TextareaOnChangeData,
  ) => {
    setDescription(data.value);
    setFieldErrors((prev) => {
      const { description: _, ...rest } = prev;
      return rest;
    });
  };
  const handleBackgroundChange = (
    _: ChangeEvent<HTMLTextAreaElement>,
    data: TextareaOnChangeData,
  ) => {
    setBackground(data.value);
  };
  const handleSelectedCollaborators = (opts: CustomOption[]) => {
    setSelectedCollaborators(opts);
  };

  const handleSelectedOwnerChange = (opts: CustomOption[]) => {
    const lastSelected = opts.length ? [opts[opts.length - 1]] : [];
    setSelectedOwner(lastSelected);
  };

  const readResourcesForPickerFieldFiltered = useCallback(
    async (q: string): Promise<IAzureUser[]> => {
      const mapped = mapAzureUsersToModel(users);
      return mapped.filter((r) =>
        r.Name.toLowerCase().includes(q.toLowerCase()),
      );
    },
    [users],
  );

  const nameValidation = getFieldValidation(
    name.length,
    FIELD_LIMITS.NAME_MAX,
    FIELD_LIMITS.NAME_WARN,
  );
  const descValidation = getFieldValidation(
    description.length,
    FIELD_LIMITS.DESCRIPTION_MAX,
    FIELD_LIMITS.DESCRIPTION_WARN,
  );
  const bgValidation = getFieldValidation(
    background.length,
    FIELD_LIMITS.BACKGROUND_MAX,
    FIELD_LIMITS.BACKGROUND_WARN,
  );

  const hasIncompleteDetails =
    !name.trim() ||
    !description.trim() ||
    !client.trim() ||
    nameValidation.state === "error" ||
    descValidation.state === "error";

  const hasEmptyDemographicQuestion =
    demographicsEnabled &&
    demographicsQuestions.some(
      (id) =>
        demographicsQuestionOverrides[id] !== undefined &&
        demographicsQuestionOverrides[id].trim() === "",
    );

  const handleDropzoneFiles = (files: File[]): void => {
    const totalExisting = existingAttachments.length + localFiles.length;
    if (totalExisting >= 1) {
      setFieldErrors((prev) => ({
        ...prev,
        attachments:
          "Only 1 attachment is allowed. Remove the existing attachment before adding a new one.",
      }));
      return;
    }
    const existingNames = new Set(localFiles.map((v) => v.file.name));
    const unique = files
      .filter((f) => !existingNames.has(f.name))
      .slice(0, 1 - totalExisting)
      .map((f) => ({ file: f }));
    if (unique.length > 0) {
      setLocalFiles((prev) => [...prev, ...unique]);
      setFieldErrors((prev) => {
        const { attachments: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleDownloadAttachment = useCallback(async (file: IFile) => {
    if (!file.url) return;
    setIsDownloading(true);
    try {
      await downloadBlobAsFile(
        file.url,
        file.filename.split("/").pop() || file.filename,
      );
    } catch {
      setFieldErrors((prev) => ({
        ...prev,
        attachments: "Download failed. Please try again.",
      }));
    } finally {
      setIsDownloading(false);
    }
  }, []);

  const handleDownloadLocalFile = useCallback((file: File) => {
    setIsDownloading(true);
    try {
      triggerBrowserDownload(file, file.name);
    } finally {
      setIsDownloading(false);
    }
  }, []);

  const handleBeginningMessageChange = (
    _: unknown,
    value: string | undefined,
  ): void => {
    setBeginningMessage(value || ConstantValues.EMPTY_STRING);
  };

  const handleEndingMessageChange = (
    _: unknown,
    value: string | undefined,
  ): void => {
    setEndingMessage(value || ConstantValues.EMPTY_STRING);
  };

  const handleBrandingToggle = (checked: boolean) => {
    setCustomBrandingEnabled(checked);
    if (!checked && activeTab === "branding") {
      setActiveTab("messages");
    }
  };

  const handleDemographicsToggle = (checked: boolean) => {
    setDemographicsEnabled(checked);
    if (!checked && activeTab === "demographics") {
      setActiveTab(customBrandingEnabled ? "branding" : "messages");
    }
  };

  const handleLogoFileSelect = (files: File[]): void => {
    if (files.length > 0) {
      const file = files[0];
      setLogoFile(file);
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.logo;
        return next;
      });
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setHeaderLogoUrl(e.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoFileRemove = (): void => {
    setLogoFile(null);
    setHeaderLogoUrl(ConstantValues.EMPTY_STRING);
  };

  const navigatePrev = (): void => {
    const idx = tabOrder.indexOf(activeTab);
    if (idx > 0) navigateTab(tabOrder[idx - 1]);
  };

  const navigateNext = (): void => {
    const idx = tabOrder.indexOf(activeTab);
    if (idx < tabOrder.length - 1) navigateTab(tabOrder[idx + 1]);
  };

  return (
    <Dialog
      open={props.isOpen}
      modalType="modal"
      onOpenChange={(_, data) => {
        if (!data.open && !isLoading) props.onDismiss(false);
      }}
    >
      <DialogSurface
        className={mergeClasses(
          dialogFooterStyles.dialogSurface,
          styles.dialogSurfaceOverride,
        )}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={() => props.onDismiss(false)}
                disabled={isLoading}
              />
            }
          >
            {isEditMode ? "Edit Diagnostic" : "Create Diagnostic"}
          </DialogTitle>
          <DialogContent
            className={mergeClasses(
              contentStyles.tabbedDialogContent,
              styles.dialogContent,
              isLoading && styles.dialogContentLoading,
            )}
          >
            <DialogTabs
              tabs={[
                { key: "details", label: "Details" },
                {
                  key: "context",
                  label: "Context",
                  tooltip:
                    "The context helps provide additional context when creating questions for each objective.",
                },
                { key: "messages", label: "Messages" },
                ...(customBrandingEnabled
                  ? [{ key: "branding", label: "Branding" }]
                  : []),
                ...(demographicsEnabled
                  ? [{ key: "demographics", label: "Demographics" }]
                  : []),
              ]}
              activeTab={activeTab}
              onTabClick={(tab) => navigateTab(tab as DiagnosticTab)}
            />
            <div
              className={mergeClasses(
                contentStyles.formContainer,
                styles.scrollableContent,
              )}
            >
              {isLoading && (
                <div
                  className={contentStyles.formLoadingOverlay}
                  aria-live="polite"
                  aria-label="Saving, please wait"
                >
                  <Spinner label="Saving diagnostic..." />
                </div>
              )}

              {activeTab === "details" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  aria-labelledby="details-tab"
                >
                  <Field
                    label="Name"
                    required
                    validationState={
                      fieldErrors.name
                        ? "error"
                        : nameValidation.state === "none"
                          ? undefined
                          : nameValidation.state
                    }
                    validationMessage={
                      fieldErrors.name || nameValidation.message
                    }
                  >
                    <Input
                      value={name}
                      onChange={handleChange(setName, "name")}
                      placeholder="Enter diagnostic name..."
                      disabled={isLoading}
                      maxLength={FIELD_LIMITS.NAME_MAX}
                    />
                  </Field>

                  <Field
                    label={
                      <LabelWithInfo
                        label="Engagement Details"
                        info="Project Code and other relevant details for tracking purposes"
                        required
                      />
                    }
                    validationState={
                      fieldErrors.description
                        ? "error"
                        : descValidation.state === "none"
                          ? undefined
                          : descValidation.state
                    }
                    validationMessage={
                      fieldErrors.description || descValidation.message
                    }
                  >
                    <Textarea
                      value={description}
                      maxLength={FIELD_LIMITS.DESCRIPTION_MAX}
                      rows={3}
                      minLength={2}
                      onChange={handleDescriptionChange}
                      placeholder="Describe engagement details..."
                      disabled={isLoading}
                    />
                  </Field>

                  <Field
                    label={
                      <LabelWithInfo
                        label="Client"
                        info="Who the diagnostic is for (company or business unit)."
                        required
                      />
                    }
                    validationState={fieldErrors.client ? "error" : undefined}
                    validationMessage={fieldErrors.client}
                  >
                    <Input
                      value={client}
                      onChange={handleChange(setClient, "client")}
                      placeholder="Enter client name..."
                      disabled={isLoading}
                    />
                  </Field>

                  {props.isDiagnosticsAdmin && (
                    <Field
                      label={
                        <LabelWithInfo
                          label="Owner"
                          info="The person who owns this diagnostic. Only admins can set or reassign ownership."
                          required
                        />
                      }
                      validationState={fieldErrors.owner ? "error" : undefined}
                      validationMessage={fieldErrors.owner}
                    >
                      <PeoplePicker
                        id="owner"
                        handleSelectedItems={(items) => {
                          handleSelectedOwnerChange(items);
                          setFieldErrors((prev) => {
                            const { owner: _, ...rest } = prev;
                            return rest;
                          });
                        }}
                        disabled={isLoading}
                        hasOnlyOurUsers
                        hasSingleSelection
                        readResourcesForPickerFieldFiltered={
                          readResourcesForPickerFieldFiltered
                        }
                        selectedOptions={selectedOwner}
                      />
                    </Field>
                  )}

                  <Field
                    label={
                      <LabelWithInfo
                        label="Collaborators"
                        info="People who can view, edit, and delete this diagnostic."
                      />
                    }
                  >
                    <PeoplePicker
                      id="collaborators"
                      handleSelectedItems={handleSelectedCollaborators}
                      disabled={isLoading}
                      hasOnlyOurUsers={true}
                      readResourcesForPickerFieldFiltered={
                        readResourcesForPickerFieldFiltered
                      }
                      selectedOptions={selectedCollaborators}
                    />
                  </Field>

                  <CustomToggle
                    checked={customBrandingEnabled}
                    onChange={handleBrandingToggle}
                    label="Custom Branding"
                    hint="Enable to customize the header color and logo shown in the external interview screens."
                    disabled={isLoading}
                  />

                  <CustomToggle
                    checked={demographicsEnabled}
                    onChange={handleDemographicsToggle}
                    label="Demographics"
                    hint="Enable to collect demographic information from interviewees during the AI Interview."
                    disabled={isLoading}
                  />
                </div>
              )}

              {activeTab === "context" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  aria-labelledby="context-tab"
                >
                  <Field
                    label={
                      <LabelWithInfo
                        label="Context"
                        info="High-level context about the diagnostic (client, engagement, purpose, topic, etc.)"
                      />
                    }
                    validationState={bgValidation.state}
                    validationMessage={bgValidation.message}
                  >
                    <Textarea
                      value={background}
                      maxLength={FIELD_LIMITS.BACKGROUND_MAX}
                      rows={4}
                      onChange={handleBackgroundChange}
                      placeholder="Provide context information..."
                      disabled={isLoading}
                    />
                  </Field>

                  <LabelWithInfo
                    label="Attachments"
                    info="Only 1 attachment is allowed. Supported formats: PDF, Word, Excel, PowerPoint, images, and text files."
                  />
                  <FileDropzone
                    onFilesSelected={handleDropzoneFiles}
                    acceptedExtensions={ALLOWED_FILE_EXTENSIONS}
                    disabled={isLoading}
                    onValidationError={(msg) =>
                      setFieldErrors((prev) => ({
                        ...prev,
                        attachments: msg,
                      }))
                    }
                  />
                  {fieldErrors.attachments && (
                    <Text size={200} className={styles.errorMessage}>
                      {fieldErrors.attachments}
                    </Text>
                  )}

                  {existingAttachments.length > 0 && (
                    <div className={fileCardStyles.fileList}>
                      {existingAttachments.map((f) => (
                        <div
                          key={f.filename}
                          className={fileCardStyles.fileCard}
                        >
                          <span className={fileCardStyles.fileIcon}>
                            <DocumentRegular />
                          </span>
                          <div className={fileCardStyles.fileInfo}>
                            <Tooltip
                              content={
                                f.filename.split("/").pop() || f.filename
                              }
                              relationship="description"
                            >
                              <Text className={fileCardStyles.fileName}>
                                {f.filename.split("/").pop() || f.filename}
                              </Text>
                            </Tooltip>
                          </div>
                          <div className={fileCardStyles.fileActions}>
                            <Tooltip content="Download" relationship="label">
                              <Button
                                appearance="subtle"
                                size="small"
                                className={fileCardStyles.fileDownloadButton}
                                icon={
                                  isDownloading ? (
                                    <Spinner size="tiny" />
                                  ) : (
                                    <ArrowDownloadRegular />
                                  )
                                }
                                disabled={isDownloading}
                                aria-label={`Download ${f.filename.split("/").pop()}`}
                                onClick={() => handleDownloadAttachment(f)}
                              />
                            </Tooltip>
                            <Tooltip content="Remove" relationship="label">
                              <Button
                                appearance="subtle"
                                size="small"
                                icon={<DeleteRegular />}
                                className={fileCardStyles.fileDeleteButton}
                                aria-label={`Remove ${f.filename.split("/").pop()}`}
                                onClick={() =>
                                  setExistingAttachments((prev) =>
                                    prev.filter(
                                      (e) => e.filename !== f.filename,
                                    ),
                                  )
                                }
                              />
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {localFiles.length > 0 && (
                    <div className={fileCardStyles.fileList}>
                      {localFiles.map((f) => (
                        <div
                          key={f.file.name}
                          className={fileCardStyles.fileCard}
                        >
                          <span className={fileCardStyles.fileIcon}>
                            <DocumentRegular />
                          </span>
                          <div className={fileCardStyles.fileInfo}>
                            <Tooltip
                              content={f.file.name}
                              relationship="description"
                            >
                              <Text className={fileCardStyles.fileName}>
                                {f.file.name}
                              </Text>
                            </Tooltip>
                          </div>
                          <div className={fileCardStyles.fileActions}>
                            <Tooltip content="Download" relationship="label">
                              <Button
                                appearance="subtle"
                                className={fileCardStyles.fileDownloadButton}
                                size="small"
                                icon={
                                  isDownloading ? (
                                    <Spinner size="tiny" />
                                  ) : (
                                    <ArrowDownloadRegular />
                                  )
                                }
                                disabled={isDownloading}
                                aria-label={`Download ${f.file.name}`}
                                onClick={() => handleDownloadLocalFile(f.file)}
                              />
                            </Tooltip>
                            <Tooltip content="Remove" relationship="label">
                              <Button
                                appearance="subtle"
                                size="small"
                                icon={<DeleteRegular />}
                                className={fileCardStyles.fileDeleteButton}
                                aria-label={`Remove ${f.file.name}`}
                                onClick={() =>
                                  setLocalFiles((prev) =>
                                    prev.filter(
                                      (v) => v.file.name !== f.file.name,
                                    ),
                                  )
                                }
                              />
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "messages" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  aria-labelledby="messages-tab"
                >
                  <Field
                    label={
                      <LabelWithInfo
                        label="Beginning Message"
                        info="Shown to participants on landing page to set context of the interview. Can be used to set expectations or provide additional instructions."
                      />
                    }
                    className={styles.messagesEditor}
                  >
                    <RichTextField
                      value={beginningMessage}
                      editable
                      onChange={handleBeginningMessageChange}
                      type={ColumnTypes.RichText}
                    />
                  </Field>

                  <Field
                    label={
                      <LabelWithInfo
                        label="Ending Message"
                        info="Shown after completion. Use it for thanks, next steps, or links."
                      />
                    }
                    className={styles.messagesEditor}
                  >
                    <RichTextField
                      value={endingMessage}
                      editable
                      onChange={handleEndingMessageChange}
                      type={ColumnTypes.RichText}
                    />
                  </Field>
                </div>
              )}

              {activeTab === "branding" && (
                <div
                  className={styles.brandingTabLayout}
                  role="tabpanel"
                  aria-labelledby="branding-tab"
                >
                  <div className={styles.brandingFormColumn}>
                    <Field
                      label={
                        <LabelWithInfo
                          label="Header Logo"
                          info="Logo shown in the interview header. PNG, JPG, or SVG. Max 2 MB. Defaults to North Highland logo."
                        />
                      }
                    >
                      {headerLogoUrl ? (
                        <div className={styles.logoPreviewWrapper}>
                          <img
                            src={headerLogoUrl}
                            alt="Header logo preview"
                            className={styles.logoPreview}
                          />
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<DeleteRegular />}
                            onClick={handleLogoFileRemove}
                            aria-label="Remove logo"
                          >
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <>
                          <FileDropzone
                            onFilesSelected={handleLogoFileSelect}
                            acceptedExtensions={[
                              ".png",
                              ".jpg",
                              ".jpeg",
                              ".svg",
                            ]}
                            multiple={false}
                            onValidationError={() =>
                              setFieldErrors((prev) => ({
                                ...prev,
                                logo: "Unsupported file type. Please upload a PNG, JPG, or SVG image.",
                              }))
                            }
                          />
                          {fieldErrors.logo && (
                            <Text size={200} className={styles.errorMessage}>
                              {fieldErrors.logo}
                            </Text>
                          )}
                        </>
                      )}
                    </Field>

                    <Field
                      label={
                        <LabelWithInfo
                          label="Header Color"
                          info="Background color for the interview header. Defaults to North Highland navy."
                        />
                      }
                    >
                      <div className={styles.colorPickerRow}>
                        <input
                          type="color"
                          value={headerColor}
                          onChange={(e) => setHeaderColor(e.target.value)}
                          className={styles.colorInput}
                          aria-label="Header color picker"
                        />
                        <Input
                          value={headerColor}
                          onChange={(_, d) => setHeaderColor(d.value)}
                          placeholder={designTokens.primary[900]}
                          maxLength={7}
                          className={styles.hexInput}
                        />
                        <Button
                          appearance="subtle"
                          size="small"
                          onClick={() =>
                            setHeaderColor(designTokens.primary[900])
                          }
                          disabled={
                            headerColor === designTokens.primary[900] ||
                            isLoading
                          }
                        >
                          Reset to default
                        </Button>
                      </div>
                    </Field>

                    <Field
                      label={
                        <LabelWithInfo
                          label="Accent Color"
                          info="Colors the progress bar, badges, send button, and question shelf. Defaults to North Highland teal."
                        />
                      }
                    >
                      <div className={styles.colorPickerRow}>
                        <input
                          type="color"
                          value={accentColor}
                          onChange={(e) => setAccentColor(e.target.value)}
                          className={styles.colorInput}
                          aria-label="Accent color picker"
                        />
                        <Input
                          value={accentColor}
                          onChange={(_, d) => setAccentColor(d.value)}
                          placeholder={designTokens.accent[500]}
                          maxLength={7}
                          className={styles.hexInput}
                        />
                        <Button
                          appearance="subtle"
                          size="small"
                          onClick={() =>
                            setAccentColor(designTokens.accent[500])
                          }
                          disabled={
                            accentColor.toLowerCase() ===
                              designTokens.accent[500] || isLoading
                          }
                        >
                          Reset to default
                        </Button>
                      </div>
                    </Field>
                  </div>
                  <div className={styles.brandingPreviewColumn}>
                    <Caption1 as="span" className={styles.previewLabel}>
                      Preview
                    </Caption1>
                    {!isLoading && (
                      <BrandingPreview
                        headerColor={headerColor}
                        headerLogoUrl={headerLogoUrl || undefined}
                        accentPalette={previewPalette}
                      />
                    )}
                  </div>
                </div>
              )}

              {activeTab === "demographics" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  aria-labelledby="demographics-tab"
                >
                  <DemographicsTab
                    selectedQuestions={demographicsQuestions}
                    onChange={setDemographicsQuestions}
                    disabled={isLoading}
                    placement={demographicsPlacement}
                    onPlacementChange={setDemographicsPlacement}
                    overrides={demographicsQuestionOverrides}
                    onOverrideChange={setDemographicsQuestionOverrides}
                  />
                </div>
              )}
            </div>
          </DialogContent>
        </DialogBody>
        {!isLoading && (
          <div className={dialogFooterStyles.dialogFooter}>
            {activeTab !== "details" && (
              <DialogFooterAction
                icon={<ArrowLeftRegular />}
                tooltip="Back"
                dimmed={hasIncompleteDetails}
                ariaLabel={
                  hasIncompleteDetails
                    ? "Back (complete required fields first)"
                    : "Back"
                }
                onClick={navigatePrev}
              />
            )}
            {activeTab !== tabOrder[tabOrder.length - 1] && (
              <DialogFooterAction
                icon={<ArrowRightRegular />}
                tooltip="Next"
                dimmed={hasIncompleteDetails}
                ariaLabel={
                  hasIncompleteDetails
                    ? "Next (complete required fields first)"
                    : "Next"
                }
                onClick={navigateNext}
              />
            )}
            {activeTab === tabOrder[tabOrder.length - 1] && (
              <DialogFooterAction
                icon={<CheckmarkRegular />}
                tooltip={
                  hasEmptyDemographicQuestion
                    ? "Selected demographic questions can't be empty"
                    : "Save"
                }
                dimmed={hasIncompleteDetails || hasEmptyDemographicQuestion}
                disabled={hasEmptyDemographicQuestion}
                ariaLabel={
                  hasEmptyDemographicQuestion
                    ? "Save (selected demographic questions can't be empty)"
                    : hasIncompleteDetails
                      ? "Save (complete required fields first)"
                      : "Save"
                }
                onClick={handleSave}
              />
            )}
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
